import { useState, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, Download } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

export type ImportType = 'transactions' | 'debts' | 'goals';

interface ImportConfig {
  type: ImportType;
  requiredColumns: string[];
  optionalColumns?: string[];
  validateRow: (row: Record<string, unknown>) => { valid: boolean; errors: string[] };
  transformRow: (row: Record<string, unknown>) => Record<string, unknown>;
}

interface DataImportDialogProps {
  type: ImportType;
  onImport: (data: Record<string, unknown>[]) => Promise<void>;
  trigger?: React.ReactNode;
}

// Mapeo de tipos en español a inglés
const TYPE_MAP_ES_TO_EN: Record<string, string> = {
  'ingreso': 'income',
  'gasto_fijo': 'fixed_expense',
  'gasto_variable': 'variable_expense',
  'ahorro': 'saving',
  'inversion': 'investment',
  'inversión': 'investment',
  'pago_deuda': 'debt_payment',
};

// Mapeo de categorías en español a inglés
const CATEGORY_MAP_ES_TO_EN: Record<string, string> = {
  // Ingresos
  'salario': 'salary',
  'bono': 'bonus',
  'negocio': 'business',
  'freelance': 'freelance',
  'alquileres': 'rental_income',
  'dividendos': 'dividends',
  'ventas': 'sales',
  'reembolsos': 'reimbursements',
  'otros_ingresos': 'other_income',
  // Gastos fijos
  'vivienda': 'housing',
  'servicios': 'utilities',
  'seguros': 'insurance',
  'suscripciones': 'subscriptions',
  'transporte_fijo': 'transport_fixed',
  // Gastos variables
  'alimentacion': 'food',
  'alimentación': 'food',
  'transporte': 'transport',
  'entretenimiento': 'entertainment',
  'salud': 'health',
  'educacion': 'education',
  'educación': 'education',
  'cursos': 'courses',
  'mentorias': 'mentoring',
  'mentorías': 'mentoring',
  'ropa': 'clothing',
  'personal': 'personal',
  'belleza': 'beauty',
  'regalos': 'gifts',
  'donaciones': 'donations',
  'hobbies': 'hobbies',
  'viajes': 'travel',
  'mascotas': 'pets',
  'mantenimiento_casa': 'home_maintenance',
  'jardineria': 'gardening',
  'jardinería': 'gardening',
  'limpieza': 'cleaning',
  // Ahorro
  'fondo_emergencia': 'emergency_fund',
  'fondo_metas': 'sinking_fund',
  'ahorro_general': 'general_savings',
  // Inversión
  'etf': 'investment_etf',
  'cripto': 'investment_crypto',
  'acciones': 'investment_stocks',
  'bienes_raices': 'real_estate',
  'fondos_pension': 'pension_funds',
  'bonos': 'bonds',
  'oro': 'gold',
  // Pago deudas
  'tarjeta_credito': 'credit_card',
  'prestamo': 'loan',
  'préstamo': 'loan',
  'hipoteca': 'mortgage',
  'otra_deuda': 'other_debt',
};

// Mapeo de métodos de pago en español a inglés
const PAYMENT_METHOD_MAP_ES_TO_EN: Record<string, string> = {
  'efectivo': 'cash',
  'tarjeta_debito': 'debit_card',
  'tarjeta_credito': 'credit_card',
  'transferencia': 'transfer',
  'cheque': 'check',
  'otro': 'other',
};

const IMPORT_CONFIGS: Record<ImportType, ImportConfig> = {
  transactions: {
    type: 'transactions',
    requiredColumns: ['tipo', 'categoria', 'monto', 'fecha'],
    optionalColumns: ['metodo_pago', 'notas', 'moneda'],
    validateRow: (row) => {
      const errors: string[] = [];
      const validTypesES = Object.keys(TYPE_MAP_ES_TO_EN);
      const tipoNorm = String(row.tipo).toLowerCase().trim();
      
      if (!row.tipo || !validTypesES.includes(tipoNorm)) {
        errors.push(`Tipo inválido. Usa: ${validTypesES.join(', ')}`);
      }
      if (!row.monto || isNaN(Number(row.monto)) || Number(row.monto) <= 0) {
        errors.push('Monto debe ser un número positivo');
      }
      if (!row.fecha) {
        errors.push('Fecha es requerida');
      }
      if (!row.categoria) {
        errors.push('Categoría es requerida');
      }
      
      return { valid: errors.length === 0, errors };
    },
    transformRow: (row) => {
      const tipoNorm = String(row.tipo).toLowerCase().trim();
      const categoriaNorm = String(row.categoria).toLowerCase().trim();
      const metodoPagoNorm = row.metodo_pago ? String(row.metodo_pago).toLowerCase().trim() : 'efectivo';
      
      return {
        type: TYPE_MAP_ES_TO_EN[tipoNorm] || tipoNorm,
        category: CATEGORY_MAP_ES_TO_EN[categoriaNorm] || categoriaNorm,
        amount: Number(row.monto),
        date: formatDate(row.fecha),
        payment_method: PAYMENT_METHOD_MAP_ES_TO_EN[metodoPagoNorm] || metodoPagoNorm,
        notes: row.notas ? String(row.notas) : null,
        currency: row.moneda ? String(row.moneda).toUpperCase() : 'CRC',
      };
    },
  },
  debts: {
    type: 'debts',
    requiredColumns: ['nombre', 'saldo_inicial', 'saldo_actual', 'pago_minimo'],
    optionalColumns: ['tasa_interes', 'acreedor', 'dia_vencimiento'],
    validateRow: (row) => {
      const errors: string[] = [];
      
      if (!row.nombre) {
        errors.push('Nombre es requerido');
      }
      if (!row.saldo_inicial || isNaN(Number(row.saldo_inicial)) || Number(row.saldo_inicial) <= 0) {
        errors.push('Saldo inicial debe ser un número positivo');
      }
      if (row.saldo_actual === undefined || isNaN(Number(row.saldo_actual)) || Number(row.saldo_actual) < 0) {
        errors.push('Saldo actual debe ser un número >= 0');
      }
      if (!row.pago_minimo || isNaN(Number(row.pago_minimo)) || Number(row.pago_minimo) <= 0) {
        errors.push('Pago mínimo debe ser un número positivo');
      }
      
      return { valid: errors.length === 0, errors };
    },
    transformRow: (row) => ({
      name: String(row.nombre),
      initial_balance: Number(row.saldo_inicial),
      current_balance: Number(row.saldo_actual),
      minimum_payment: Number(row.pago_minimo),
      interest_rate: row.tasa_interes ? Number(row.tasa_interes) : 0,
      creditor: row.acreedor ? String(row.acreedor) : null,
      due_day: row.dia_vencimiento ? Number(row.dia_vencimiento) : null,
    }),
  },
  goals: {
    type: 'goals',
    requiredColumns: ['nombre', 'monto_objetivo'],
    optionalColumns: ['monto_actual', 'fecha_limite', 'prioridad', 'estado'],
    validateRow: (row) => {
      const errors: string[] = [];
      
      if (!row.nombre) {
        errors.push('Nombre es requerido');
      }
      if (!row.monto_objetivo || isNaN(Number(row.monto_objetivo)) || Number(row.monto_objetivo) <= 0) {
        errors.push('Monto objetivo debe ser un número positivo');
      }
      if (row.monto_actual !== undefined && (isNaN(Number(row.monto_actual)) || Number(row.monto_actual) < 0)) {
        errors.push('Monto actual debe ser un número >= 0');
      }
      
      return { valid: errors.length === 0, errors };
    },
    transformRow: (row) => ({
      name: String(row.nombre),
      target_amount: Number(row.monto_objetivo),
      current_amount: row.monto_actual ? Number(row.monto_actual) : 0,
      deadline: row.fecha_limite ? formatDate(row.fecha_limite) : null,
      priority: row.prioridad ? Number(row.prioridad) : 3,
      status: row.estado ? String(row.estado).toLowerCase() : 'active',
    }),
  },
};

function formatDate(value: unknown): string {
  if (!value) return new Date().toISOString().split('T')[0];
  
  // If it's an Excel serial date number
  if (typeof value === 'number') {
    // Excel serial date: days since Dec 30, 1899
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
    return date.toISOString().split('T')[0];
  }
  
  // If it's a Date object (ExcelJS returns Date objects for date cells)
  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }
  
  // If it's a string, try to parse it
  const dateStr = String(value).trim();
  
  // Try DD-MM-YYYY or DD/MM/YYYY format first (día-mes-año)
  const ddmmyyyyMatch = dateStr.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (ddmmyyyyMatch) {
    const [, day, month, year] = ddmmyyyyMatch;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  
  // Try YYYY-MM-DD format
  const yyyymmddMatch = dateStr.match(/^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/);
  if (yyyymmddMatch) {
    const [, year, month, day] = yyyymmddMatch;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  
  // Fallback to Date parsing
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  
  return new Date().toISOString().split('T')[0];
}

const TYPE_LABELS: Record<ImportType, string> = {
  transactions: 'Transacciones',
  debts: 'Deudas',
  goals: 'Metas Financieras',
};

export function DataImportDialog({ type, onImport, trigger }: DataImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<Record<string, unknown>[]>([]);
  const [validationResults, setValidationResults] = useState<{ valid: boolean; errors: string[] }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState(false);

  const config = IMPORT_CONFIGS[type];

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setImportSuccess(false);
    setIsProcessing(true);

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(data);
      const worksheet = workbook.worksheets[0];
      
      if (!worksheet) {
        setError('El archivo está vacío o no tiene hojas válidas');
        setParsedData([]);
        setValidationResults([]);
        return;
      }

      // Get headers from first row
      const headers: string[] = [];
      worksheet.getRow(1).eachCell((cell, colNumber) => {
        headers[colNumber - 1] = String(cell.value || '').toLowerCase().trim().replace(/\s+/g, '_');
      });

      // Convert rows to JSON
      const jsonData: Record<string, unknown>[] = [];
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // Skip header row
        const rowData: Record<string, unknown> = {};
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber - 1];
          if (header) {
            rowData[header] = cell.value;
          }
        });
        if (Object.keys(rowData).length > 0) {
          jsonData.push(rowData);
        }
      });

      if (jsonData.length === 0) {
        setError('El archivo está vacío o no tiene datos válidos');
        setParsedData([]);
        setValidationResults([]);
        return;
      }

      // Normalize column names (lowercase, trim)
      const normalizedData = jsonData.map((row: unknown) => {
        const normalized: Record<string, unknown> = {};
        const typedRow = row as Record<string, unknown>;
        Object.entries(typedRow).forEach(([key, value]) => {
          normalized[key.toLowerCase().trim().replace(/\s+/g, '_')] = value;
        });
        return normalized;
      });

      // Validate each row
      const results = normalizedData.map(row => config.validateRow(row));
      
      setParsedData(normalizedData);
      setValidationResults(results);
    } catch (err) {
      console.error('Error parsing file:', err);
      setError('Error al leer el archivo. Asegúrate de que sea un archivo Excel (.xlsx, .xls) o CSV válido.');
      setParsedData([]);
      setValidationResults([]);
    } finally {
      setIsProcessing(false);
    }
  }, [config]);

  const handleImport = async () => {
    const validRows = parsedData.filter((_, index) => validationResults[index]?.valid);
    
    if (validRows.length === 0) {
      setError('No hay filas válidas para importar');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const transformedData = validRows.map(row => config.transformRow(row));
      await onImport(transformedData);
      setImportSuccess(true);
      
      // Reset after success
      setTimeout(() => {
        setOpen(false);
        setFile(null);
        setParsedData([]);
        setValidationResults([]);
        setImportSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Import error:', err);
      setError('Error al importar los datos. Intenta de nuevo.');
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [...config.requiredColumns, ...(config.optionalColumns || [])];
    
    // Obtener mes y año actual para las fechas de ejemplo
    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYear = now.getFullYear();
    
    // Función helper para crear fecha en formato DD-MM-YYYY
    const makeDate = (day: number) => `${String(day).padStart(2, '0')}-${currentMonth}-${currentYear}`;
    
    // Example data for each type (en español con formato DD-MM-YYYY del mes actual)
    const exampleData: Record<ImportType, string[][]> = {
      transactions: [
        headers,
        ['ingreso', 'salario', '500000', makeDate(1), 'transferencia', '', 'CRC'],
        ['ingreso', 'bono', '100000', makeDate(15), 'transferencia', 'Bono anual', 'CRC'],
        ['gasto_fijo', 'vivienda', '150000', makeDate(5), 'transferencia', 'Alquiler mensual', 'CRC'],
        ['gasto_fijo', 'servicios', '25000', makeDate(10), 'tarjeta_debito', 'Electricidad', 'CRC'],
        ['gasto_fijo', 'suscripciones', '15000', makeDate(1), 'tarjeta_credito', 'Netflix + Spotify', 'CRC'],
        ['gasto_variable', 'alimentacion', '80000', makeDate(8), 'tarjeta_debito', 'Supermercado', 'CRC'],
        ['gasto_variable', 'transporte', '30000', makeDate(12), 'efectivo', 'Gasolina', 'CRC'],
        ['gasto_variable', 'entretenimiento', '20000', makeDate(20), 'tarjeta_credito', 'Cine y restaurante', 'CRC'],
        ['gasto_variable', 'salud', '15000', makeDate(18), 'tarjeta_debito', 'Farmacia', 'CRC'],
        ['ahorro', 'fondo_emergencia', '50000', makeDate(1), 'transferencia', 'Ahorro mensual', 'CRC'],
        ['ahorro', 'fondo_metas', '25000', makeDate(1), 'transferencia', 'Fondo vacaciones', 'CRC'],
        ['inversion', 'etf', '30000', makeDate(5), 'transferencia', 'Inversión ETF', 'USD'],
        ['pago_deuda', 'tarjeta_credito', '45000', makeDate(25), 'transferencia', 'Pago tarjeta', 'CRC'],
      ],
      debts: [
        headers,
        ['Tarjeta Visa', '500000', '350000', '25000', '18.5', 'BAC', '25'],
        ['Tarjeta Mastercard', '300000', '180000', '15000', '22', 'BCR', '15'],
        ['Préstamo Personal', '1000000', '750000', '50000', '12', 'Banco Nacional', '1'],
        ['Préstamo Auto', '5000000', '3500000', '150000', '10', 'Scotiabank', '10'],
      ],
      goals: [
        headers,
        ['Fondo de Emergencia', '1500000', '500000', `31-12-${currentYear}`, '1', 'active'],
        ['Vacaciones Europa', '2000000', '300000', `30-06-${currentYear + 1}`, '2', 'active'],
        ['Enganche Casa', '10000000', '1000000', `31-12-${currentYear + 2}`, '1', 'active'],
        ['Nuevo Auto', '5000000', '800000', `31-12-${currentYear + 1}`, '3', 'active'],
      ],
    };
    
    // Add instructions sheet
    const instructionsData = type === 'transactions' ? [
      ['=== INSTRUCCIONES PARA IMPORTAR TRANSACCIONES ==='],
      [''],
      ['COLUMNA', 'DESCRIPCIÓN', 'VALORES PERMITIDOS'],
      ['tipo', 'Tipo de movimiento financiero', 'ingreso, gasto_fijo, gasto_variable, ahorro, inversion, pago_deuda'],
      ['categoria', 'Categoría según el tipo', 'Ver hoja "Categorías"'],
      ['monto', 'Cantidad en números', 'Solo números positivos (ej: 50000)'],
      ['fecha', 'Fecha de la transacción', 'Formato: DD-MM-YYYY (ej: 15-01-2024)'],
      ['metodo_pago', '(Opcional) Método de pago', 'efectivo, tarjeta_debito, tarjeta_credito, transferencia, cheque, otro'],
      ['notas', '(Opcional) Descripción', 'Texto libre'],
      ['moneda', '(Opcional) Moneda', 'CRC (colones) o USD (dólares). Default: CRC'],
    ] : type === 'debts' ? [
      ['=== INSTRUCCIONES PARA IMPORTAR DEUDAS ==='],
      [''],
      ['COLUMNA', 'DESCRIPCIÓN', 'VALORES PERMITIDOS'],
      ['nombre', 'Nombre de la deuda', 'Texto (ej: Tarjeta Visa)'],
      ['saldo_inicial', 'Monto original de la deuda', 'Número positivo'],
      ['saldo_actual', 'Monto pendiente actual', 'Número >= 0'],
      ['pago_minimo', 'Pago mínimo mensual', 'Número positivo'],
      ['tasa_interes', '(Opcional) Tasa de interés anual %', 'Número (ej: 18.5)'],
      ['acreedor', '(Opcional) Institución acreedora', 'Texto (ej: BAC, BCR)'],
      ['dia_vencimiento', '(Opcional) Día del mes que vence', 'Número 1-31'],
    ] : [
      ['=== INSTRUCCIONES PARA IMPORTAR METAS ==='],
      [''],
      ['COLUMNA', 'DESCRIPCIÓN', 'VALORES PERMITIDOS'],
      ['nombre', 'Nombre de la meta', 'Texto (ej: Fondo de Emergencia)'],
      ['monto_objetivo', 'Meta a alcanzar', 'Número positivo'],
      ['monto_actual', '(Opcional) Progreso actual', 'Número >= 0. Default: 0'],
      ['fecha_limite', '(Opcional) Fecha límite', 'Formato: DD-MM-YYYY (ej: 31-12-2024)'],
      ['prioridad', '(Opcional) Nivel de prioridad', '1 (alta), 2 (media), 3 (baja). Default: 3'],
      ['estado', '(Opcional) Estado de la meta', 'active, completed, paused. Default: active'],
    ];
    
    // Categories sheet for transactions (en español)
    const categoriesData = [
      ['=== CATEGORÍAS POR TIPO DE TRANSACCIÓN ==='],
      [''],
      ['TIPO', 'CATEGORÍA', 'DESCRIPCIÓN'],
      ['--- INGRESOS ---', '', ''],
      ['ingreso', 'salario', 'Salario mensual'],
      ['ingreso', 'bono', 'Bonos y aguinaldo'],
      ['ingreso', 'negocio', 'Ingresos de negocio'],
      ['ingreso', 'freelance', 'Trabajo independiente / freelance'],
      ['ingreso', 'alquileres', 'Ingresos por alquileres'],
      ['ingreso', 'dividendos', 'Dividendos de inversiones'],
      ['ingreso', 'ventas', 'Ventas de productos o servicios'],
      ['ingreso', 'reembolsos', 'Reembolsos y devoluciones'],
      ['ingreso', 'otros_ingresos', 'Otros ingresos'],
      [''],
      ['--- GASTOS FIJOS ---', '', ''],
      ['gasto_fijo', 'vivienda', 'Alquiler o hipoteca'],
      ['gasto_fijo', 'servicios', 'Servicios (luz, agua, internet)'],
      ['gasto_fijo', 'seguros', 'Seguros'],
      ['gasto_fijo', 'suscripciones', 'Suscripciones (Netflix, Spotify)'],
      ['gasto_fijo', 'transporte_fijo', 'Transporte fijo (marchamo, seguro auto)'],
      [''],
      ['--- GASTOS VARIABLES ---', '', ''],
      ['gasto_variable', 'alimentacion', 'Alimentación y supermercado'],
      ['gasto_variable', 'transporte', 'Gasolina y transporte variable'],
      ['gasto_variable', 'entretenimiento', 'Entretenimiento y ocio'],
      ['gasto_variable', 'salud', 'Salud y farmacia'],
      ['gasto_variable', 'educacion', 'Educación general'],
      ['gasto_variable', 'cursos', 'Cursos y capacitaciones'],
      ['gasto_variable', 'mentorias', 'Mentorías y coaching'],
      ['gasto_variable', 'ropa', 'Ropa y calzado'],
      ['gasto_variable', 'personal', 'Gastos personales varios'],
      ['gasto_variable', 'belleza', 'Belleza y cuidado personal'],
      ['gasto_variable', 'regalos', 'Regalos'],
      ['gasto_variable', 'donaciones', 'Donaciones y caridad'],
      ['gasto_variable', 'hobbies', 'Hobbies y pasatiempos'],
      ['gasto_variable', 'viajes', 'Viajes y vacaciones'],
      ['gasto_variable', 'mascotas', 'Mascotas'],
      ['gasto_variable', 'mantenimiento_casa', 'Mantenimiento del hogar'],
      ['gasto_variable', 'jardineria', 'Jardinería'],
      ['gasto_variable', 'limpieza', 'Limpieza del hogar'],
      [''],
      ['--- AHORRO ---', '', ''],
      ['ahorro', 'fondo_emergencia', 'Fondo de emergencia'],
      ['ahorro', 'fondo_metas', 'Fondo para metas específicas'],
      ['ahorro', 'ahorro_general', 'Ahorro general'],
      [''],
      ['--- INVERSIÓN ---', '', ''],
      ['inversion', 'etf', 'ETFs y fondos indexados'],
      ['inversion', 'cripto', 'Criptomonedas'],
      ['inversion', 'acciones', 'Acciones individuales'],
      ['inversion', 'bienes_raices', 'Bienes raíces'],
      ['inversion', 'fondos_pension', 'Fondos de pensión'],
      ['inversion', 'bonos', 'Bonos'],
      ['inversion', 'oro', 'Oro y metales preciosos'],
      [''],
      ['--- PAGO DEUDAS ---', '', ''],
      ['pago_deuda', 'tarjeta_credito', 'Pago de tarjeta de crédito'],
      ['pago_deuda', 'prestamo', 'Pago de préstamo'],
      ['pago_deuda', 'hipoteca', 'Pago de hipoteca'],
      ['pago_deuda', 'otra_deuda', 'Otras deudas'],
    ];
    
    const wb = new ExcelJS.Workbook();
    
    // Helper function to add data to a worksheet
    const addDataToSheet = (ws: ExcelJS.Worksheet, data: (string | number)[][]) => {
      data.forEach(row => {
        ws.addRow(row);
      });
    };
    
    // Main data sheet with examples
    const wsData = wb.addWorksheet('Datos');
    addDataToSheet(wsData, exampleData[type]);
    
    // Instructions sheet
    const wsInstructions = wb.addWorksheet('Instrucciones');
    addDataToSheet(wsInstructions, instructionsData);
    
    // Categories sheet (only for transactions)
    if (type === 'transactions') {
      const wsCategories = wb.addWorksheet('Categorías');
      addDataToSheet(wsCategories, categoriesData);
    }
    
    // Download the file
    wb.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plantilla_${type}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  const validCount = validationResults.filter(r => r.valid).length;
  const invalidCount = validationResults.filter(r => !r.valid).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Importar
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Importar {TYPE_LABELS[type]}
          </DialogTitle>
          <DialogDescription>
            Sube un archivo Excel (.xlsx, .xls) o CSV con tus datos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="text-sm">
              <p className="font-medium">¿Necesitas una plantilla?</p>
              <p className="text-muted-foreground">Descarga el formato correcto</p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Descargar plantilla
            </Button>
          </div>

          {/* Required columns info */}
          <div className="text-sm">
            <p className="font-medium mb-1">Columnas requeridas:</p>
            <div className="flex flex-wrap gap-1">
              {config.requiredColumns.map(col => (
                <Badge key={col} variant="secondary">{col}</Badge>
              ))}
            </div>
            {config.optionalColumns && config.optionalColumns.length > 0 && (
              <>
                <p className="font-medium mt-2 mb-1">Columnas opcionales:</p>
                <div className="flex flex-wrap gap-1">
                  {config.optionalColumns.map(col => (
                    <Badge key={col} variant="outline">{col}</Badge>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* File input */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
              disabled={isProcessing}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="font-medium">
                {file ? file.name : 'Haz clic para seleccionar un archivo'}
              </p>
              <p className="text-sm text-muted-foreground">
                Formatos soportados: .xlsx, .xls, .csv
              </p>
            </label>
          </div>

          {/* Error message */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success message */}
          {importSuccess && (
            <Alert className="bg-green-500/10 border-green-500/20 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>¡Datos importados exitosamente!</AlertDescription>
            </Alert>
          )}

          {/* Validation results */}
          {parsedData.length > 0 && !importSuccess && (
            <div className="space-y-3">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  {validCount} válidas
                </span>
                {invalidCount > 0 && (
                  <span className="flex items-center gap-1 text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    {invalidCount} con errores
                  </span>
                )}
              </div>

              {/* Show errors if any */}
              {invalidCount > 0 && (
                <ScrollArea className="h-32 border rounded-lg p-3">
                  <div className="space-y-2 text-sm">
                    {validationResults.map((result, index) => 
                      !result.valid && (
                        <div key={index} className="text-destructive">
                          <span className="font-medium">Fila {index + 2}:</span> {result.errors.join(', ')}
                        </div>
                      )
                    )}
                  </div>
                </ScrollArea>
              )}

              {/* Import button */}
              <Button
                onClick={handleImport}
                disabled={validCount === 0 || isProcessing}
                className="w-full"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Importando...
                  </>
                ) : (
                  <>Importar {validCount} registro{validCount !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
